import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Typography, TextField, Box, IconButton, Skeleton, Button, Dialog, DialogContentText, DialogContent, DialogActions, Alert, ButtonGroup, Chip, debounce } from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { RichTextContainer, tiptapExtensions } from '../../app/RichTextEditor';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { blocksSelector, isDocEditable, sectionSelectors, selectDocument } from './documentSlice';
import { impossible } from '../../app/Utils';
import { loadDocBlock, updateDocBlock, updateDocSection } from './documentsApi';
import { ServerErrorMsg } from '../../app/ServerErrorMsg';
import { APIError } from '../../app/appTypes';
import { unwrapResult } from '@reduxjs/toolkit';
import { useEditor } from '@tiptap/react';
import { LoadingButton } from '@mui/lab';
import { Localized } from '@fluent/react';
import DeleteIcon from '@mui/icons-material/Delete';
import { showError } from '../notifications/notificationsSlice';
import { SectionDeleteContext } from './DocumentContent';
import { If } from '../../app/If';
import { store } from '../../app/store';
import { CopyToClipboardButton } from '../../app/components/CopyToClipboardButton';
import { sanitizeSectionCode } from './fs/DocumentSectionRules';

export const SectionContent = (props: { sectionId: string, actions: ReactNode }) => {
    const { sectionId, actions } = props
    const dispatch = useAppDispatch()
    const deleteSection = useContext(SectionDeleteContext)
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const { numeration } = section
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [tempTitle, setTempTitle] = useState(section.name);
    const [tempCode, setTempCode] = useState(section.code ?? '');
    const [isHovered, setIsHovered] = useState(false);
    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const isEditable = useAppSelector(isDocEditable)
    // Report templates reach a section by {{SectionCode:CODE}}. Only templates expose the code, and only on
    // the GROUP sections an author added; requirement-type sections keep the code of their type.
    const canEditCode = header.isTemplate === true && section.type === 'GROUP' && !section.sysSectionId
    const codeTag = section.code ? `{{SectionCode:${section.code}}}` : ''

    const handleTitleClick = () => {
        setIsEditingTitle(true);
        setTempTitle(section.name);
        setTempCode(section.code ?? '');
    };

    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempTitle(event.target.value);
    };

    const handleApprove = async () => {
        if (tempTitle) {
            try {
                await dispatch(updateDocSection({ ...section, name: tempTitle, code: canEditCode ? tempCode : section.code })).unwrap()
                setError(null)
                setIsEditingTitle(false);
            } catch (error) {
                setError(error as APIError)
            }
        }
    };

    const handleCancel = () => {
        // setTempTitle(editableTitle);
        setIsEditingTitle(false);
    };

    const handleDeleteClick = () => setDel(true)
    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setDeleting(false)
        }
    }
    const handleCancelDelClick = () => setDel(false)

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                <ServerErrorMsg err={error} />
                <Typography variant="h5" color="primary">
                    {numeration}.
                </Typography>
                {isEditingTitle ? (
                    <>
                        <TextField
                            value={tempTitle}
                            onChange={handleTitleChange}
                            autoFocus
                            variant="standard"
                            fullWidth
                        />
                        <If condition={canEditCode}>
                            <TextField
                                label={<Localized id='document-section-report-code'>Kod raportu</Localized>}
                                value={tempCode}
                                onChange={e => setTempCode(sanitizeSectionCode(e.target.value))}
                                variant="standard"
                                sx={{ minWidth: 160 }}
                            />
                        </If>
                        <IconButton color="success" onClick={handleApprove}>
                            <CheckIcon />
                        </IconButton>
                        <IconButton color="error" onClick={handleCancel}>
                            <CloseIcon />
                        </IconButton>
                    </>
                ) : (
                    <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={handleTitleClick}>
                        <Typography
                            variant="h5"
                            color="primary"
                            sx={{
                                transition: "0.2s",
                                ...(isHovered && { textDecoration: "underline", color: "secondary.main" })
                            }}
                        >
                            {section.name}
                        </Typography>
                        {isHovered && (
                            <>
                                <IconButton size="small" color="primary">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </>
                        )}
                    </Box>
                )}
                <If condition={canEditCode && !isEditingTitle && codeTag !== ''}>
                    <Chip size="small" variant="outlined" label={codeTag} />
                    <CopyToClipboardButton value={codeTag} />
                </If>
                <If condition={isEditable}>
                    <Button onClick={handleDeleteClick} color='error' variant='outlined' startIcon={<DeleteIcon />} sx={{ ml: 'auto' }} size='small'>
                        <Localized id='delete'>Usuń</Localized>
                    </Button>
                </If>
            </Box>
            <SectionContentLoader sectionId={sectionId}>
                {/* keyed so every section gets its own editor, and with it its own undo history */}
                <ContentEditor key={sectionId} sectionId={sectionId} actions={actions} />
            </SectionContentLoader>

            <Dialog open={del}>
                <DialogContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    <If condition={section.subSectionsIds.length > 0}>
                        <Alert severity='warning' variant='standard'>
                            <Localized id='document-delete-section-warning'>Podsekcje zostaną również usunięte</Localized>
                        </Alert>
                    </If>
                    <DialogContentText>
                        <Localized id="confirm-delete">
                            <span>_Czy napewno chcesz usunąć?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={deleting} onClick={handleConfirmDeleteClick}>
                        <Localized id="yes"><span>_Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={deleting} onClick={handleCancelDelClick}>
                        <Localized id="no"><span>_Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>

        </>
    );
}

const ContentEditor = (props: { sectionId: string, actions: ReactNode }) => {
    const { sectionId, actions } = props
    const dispatch = useAppDispatch()
    const { header } = useAppSelector(selectDocument).doc ?? impossible()
    const { id: docId } = header

    const [error, setError] = useState<APIError | null>(null)
    // Read once: the editor owns the content after mount, re-reading on every autosave would fight the cursor.
    const [initialContent] = useState(() => blocksSelector.selectById(store.getState(), sectionId)?.description ?? '')

    const isEditable = useAppSelector(isDocEditable)

    const autosave = useCallback(debounce((content: string) => {
        dispatch(updateDocBlock({
            sectionId,
            docId,
            description: content,
        }))
            .then(unwrapResult)
            .catch(err => setError(err as APIError))
    }, 300), [sectionId, docId])

    const editor = useEditor({
        extensions: tiptapExtensions,
        // Initial content, not a later setContent: that would be an undoable step, and undoing it blanks the section and autosaves ''.
        content: initialContent,
        editable: isEditable,
        onUpdate: (e) => {                   // callback to get the updated value
            autosave(e.editor.getHTML())
        },
    })

    return <>
        <RichTextContainer label={undefined} editor={editor} actions={actions} />

        <ServerErrorMsg err={error} />
    </>
}

export const SectionContentLoader = (props: { sectionId: string, children: ReactNode }) => {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const { header } = useAppSelector(selectDocument).doc ?? impossible()
    const { id: docId } = header
    const sectionContentExists = useAppSelector(state => blocksSelector.selectById(state, props.sectionId)) !== undefined

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<APIError | null>(null)

    useEffect(() => {
        if (!sectionContentExists) {
            setLoading(true)
            dispatch(loadDocBlock({
                docId,
                sectionId,
            }))
                .then(unwrapResult)
                .catch(e => setError(e as APIError))
                .finally(() => setLoading(false))
        }
    }, [sectionId, docId])

    if (sectionContentExists) {
        return <>{props.children}</>
    } else if (loading) {
        return <Skeleton />
    } else if (error) {
        return <ServerErrorMsg err={error} />
    } else {
        return <>test</>
    }
}
